import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Instagram, Linkedin } from 'lucide-react';
import { companyInfo } from '../data/mockData';
import { useTheme } from '../context/ThemeContext';

const Footer = () => {
  const { isDark } = useTheme();

  return (
    <footer className={`border-t ${isDark ? 'bg-neutral-900 border-neutral-800' : 'bg-neutral-50 border-neutral-200'}`}>
      {/* CTA Section */}
      <div className={isDark ? 'bg-neutral-800' : 'bg-white'}>
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <p className={`text-lg ${isDark ? 'text-neutral-300' : 'text-neutral-700'}`}>
                Call us today on <a href={`tel:${companyInfo.phone}`} className="text-lime-500 hover:text-lime-400 font-semibold transition-colors">{companyInfo.phone}</a> for a free consultation.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                className="px-8 py-3 bg-lime-500 text-black font-semibold hover:bg-lime-400 transition-all duration-300 text-center rounded-lg"
              >
                Request a Call Back
              </Link>
              <a
                href={`mailto:${companyInfo.email}`}
                className={`px-8 py-3 border-2 border-lime-500 font-semibold hover:bg-lime-500 hover:text-black transition-all duration-300 text-center rounded-lg ${isDark ? 'text-white' : 'text-neutral-900'}`}
              >
                E-Mail Us
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {/* Logo & Social */}
          <div>
            <Link to="/">
              <img 
                src={companyInfo.logoSmall} 
                alt="JPR Customs Logo" 
                className="h-16 w-auto mb-6"
              />
            </Link>
            <div className="flex gap-4 mt-6">
              <a 
                href={companyInfo.social.facebook} 
                target="_blank" 
                rel="noopener noreferrer"
                className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${isDark ? 'bg-neutral-700 text-neutral-400 hover:bg-lime-500 hover:text-black' : 'bg-neutral-200 text-neutral-600 hover:bg-lime-500 hover:text-black'}`}
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a 
                href={companyInfo.social.instagram} 
                target="_blank" 
                rel="noopener noreferrer"
                className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${isDark ? 'bg-neutral-700 text-neutral-400 hover:bg-lime-500 hover:text-black' : 'bg-neutral-200 text-neutral-600 hover:bg-lime-500 hover:text-black'}`}
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a 
                href={companyInfo.social.linkedin} 
                target="_blank" 
                rel="noopener noreferrer"
                className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${isDark ? 'bg-neutral-700 text-neutral-400 hover:bg-lime-500 hover:text-black' : 'bg-neutral-200 text-neutral-600 hover:bg-lime-500 hover:text-black'}`}
              >
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Contact Information */}
          <div>
            <h3 className={`font-semibold text-lg mb-6 ${isDark ? 'text-white' : 'text-neutral-900'}`}>Contact Information</h3>
            <ul className="space-y-4">
              <li>
                <a 
                  href="https://maps.google.com/?q=13+hindmarsh+rd+murray+bridge+sa+5253"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-start gap-3 hover:text-lime-500 transition-colors ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}
                >
                  <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span>{companyInfo.address}</span>
                </a>
              </li>
              <li>
                <a 
                  href={`tel:${companyInfo.phone}`}
                  className={`flex items-center gap-3 hover:text-lime-500 transition-colors ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}
                >
                  <Phone className="w-5 h-5 flex-shrink-0" />
                  <span>{companyInfo.phone}</span>
                </a>
              </li>
              <li>
                <a 
                  href={`mailto:${companyInfo.email}`}
                  className={`flex items-center gap-3 hover:text-lime-500 transition-colors ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}
                >
                  <Mail className="w-5 h-5 flex-shrink-0" />
                  <span>{companyInfo.email}</span>
                </a>
              </li>
            </ul>
          </div>

          {/* Hours */}
          <div>
            <h3 className={`font-semibold text-lg mb-6 ${isDark ? 'text-white' : 'text-neutral-900'}`}>Hours of Operation</h3>
            <ul className="space-y-3">
              <li className={`flex justify-between ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}>
                <span>Mon - Fri</span>
                <span className={isDark ? 'text-white' : 'text-neutral-900'}>{companyInfo.hours.weekdays}</span>
              </li>
              <li className={`flex justify-between ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}>
                <span>Sat - Sun</span>
                <span className={isDark ? 'text-white' : 'text-neutral-900'}>{companyInfo.hours.weekend}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className={`border-t ${isDark ? 'border-neutral-800' : 'border-neutral-200'}`}>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className={`flex flex-col md:flex-row items-center justify-between gap-4 text-sm ${isDark ? 'text-neutral-500' : 'text-neutral-500'}`}>
            <p>VAT 12345 | <Link to="/privacy" className="hover:text-lime-500 transition-colors">Terms of use</Link> | <Link to="/admin" className="hover:text-lime-500 transition-colors">Admin</Link></p>
            <p>© {new Date().getFullYear()} JPR Customs. All rights reserved.</p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
